import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { useTheme } from 'styled-components';
import PieChartTooltip from './Tooltip';
import { pieChartLabel } from './label';
import { PieChartProps } from './props';

/**
 * PieChart Component
 * A responsive pie chart component built using Recharts library.
 * Displays data in a circular format with customizable colors and tooltips.
 *
 * @component
 * @template T - Type of data items in the chart
 * @param {Object} props - Component props
 * @param {Array<T>} props.data - Array of data items to display in the chart
 * @param {Object} props.dataKey - Object containing x and y keys to map data
 * @param {string|number|symbol} props.dataKey.x - Key for segment labels
 * @param {string|number|symbol} props.dataKey.y - Key for segment values
 * @param {boolean} props.showAxis - Whether to show chart axes
 * @returns {JSX.Element} Rendered pie chart component
 *
 * @example
 * // Example usage with typed data
 * type DataItem = {
 *   category: string;
 *   value: number;
 *   color?: string;
 * };
 *
 * const data: DataItem[] = [
 *   { category: 'A', value: 30, color: '#ff0000' },
 *   { category: 'B', value: 70, color: '#00ff00' }
 * ];
 *
 * <PieChart<DataItem>
 *   data={data}
 *   dataKey={{ x: 'category', y: 'value' }}
 *   showAxis={false}
 * />
 */
export function PieChart<T>({
  data,
  dataKey,
  showAxis,
  angles,
}: PieChartProps<T>) {
  const theme = useTheme();

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsPieChart
        margin={{ left: 0, right: 0, bottom: 0, top: 0 }}
        {...{ overflow: 'visible' }}
      >
        <Pie
          data={data}
          dataKey={dataKey.y as string}
          nameKey={dataKey.x as string}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={90}
          labelLine={true}
          startAngle={angles?.start}
          paddingAngle={10}
          endAngle={angles?.end}
          label={angles?.start && angles?.end ? undefined : pieChartLabel}
          stroke="transparent"
        >
          {data.map((item, index) => (
            <Cell
              key={`pie-slice-${index}`}
              fill={item?.color ?? theme.vars.palette.primary.main}
            />
          ))}
        </Pie>
        <Tooltip
          cursor={false}
          content={<PieChartTooltip />}
          position={
            angles?.start && angles?.end ? { x: -100, y: 0 } : undefined
          }
        />
      </RechartsPieChart>
    </ResponsiveContainer>
  );
}
